import { catchError, debounceTime, distinctUntilChanged, exhaustMap, map, switchMap } from 'rxjs/operators';
import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { EMPTY, merge, Observable, of, Subject, timer } from 'rxjs';

import { ActivityReport } from '@app/api/activity-report/models/activity-report.models';
import { ActivityReportManagerService } from '@app/api/activity-report/activity-report-manager.service';
import { BroadcastService } from '@app/common/services/broadcast.service';
import { Constants } from '@app/shared/models/icentral-constants.model';
import { LocalCacheService } from '@app/common/services/local-cache.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export type ActivityPanelRange = 'today' | 'yesterday' | 'last7Days';

/**
 * State the header Activity panel keeps between opens (AB#20613): the chosen time range for the session, when the
 * panel was last opened, and whether newer activity has arrived since - the blue dot on the clock icon. The status
 * filter lives here too so the menu and list share it, but it goes back to all ticked each time the panel closes.
 *
 * The panel component is created on every open (toolbar-user wraps it in @if), so anything that must outlive one
 * open lives here. Every signal is read-only outside this service; state changes only through its methods (5.4).
 */
@Injectable({
    providedIn: 'root'
})
export class ActivityPanelStateService {
    static readonly PAGE_SIZE = 20;
    static readonly WINDOW_DAYS = 7;
    static readonly CHECK_INTERVAL_MS = 5 * 60 * 1000;
    /** AppSync responses come in bursts (request, then response); one check per burst is enough. */
    private static readonly APP_SYNC_DEBOUNCE_MS = 3000;

    private readonly destroyRef = inject(DestroyRef);

    private activityReportManager = inject(ActivityReportManagerService);
    private broadcastService = inject(BroadcastService);
    private localCache = inject(LocalCacheService);

    private readonly _range = signal<ActivityPanelRange>('last7Days');
    private readonly _statuses = signal<Record<string, boolean>>(ActivityPanelStateService.allStatusesTicked());
    private readonly _hasNewActivity = signal(false);
    private readonly _isPanelOpen = signal(false);
    private readonly _newActivityWhileOpen = new Subject<number>();

    readonly range = this._range.asReadonly();
    readonly statuses = this._statuses.asReadonly();
    readonly hasNewActivity = this._hasNewActivity.asReadonly();
    readonly isPanelOpen = this._isPanelOpen.asReadonly();

    /** Emits the newest activity time (epoch ms) when a check finds newer activity while the panel is open. */
    readonly newActivityWhileOpen: Observable<number> = this._newActivityWhileOpen.asObservable();

    private accountId: string | null = null;

    constructor() {
        // Session state resets when the user changes, not when the panel closes.
        merge(this.broadcastService.userLoggedIn, this.broadcastService.userLoggedOut)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.resetSession());

        const account$ = merge(
            this.broadcastService.selectedAccountChange.pipe(map((accountId: string) => accountId || null)),
            this.broadcastService.userLoggedOut.pipe(map(() => null)),
        );

        account$
            .pipe(
                distinctUntilChanged(),
                switchMap((accountId: string | null) => {
                    this.accountId = accountId;
                    this._hasNewActivity.set(false);
                    if (!accountId) { return EMPTY; }

                    // Not every activity reaches AppSync - the Account and Identity APIs write activities without
                    // publishing - so the timer is what guarantees the dot; a controller response only brings the
                    // next check forward.
                    return merge(timer(0, ActivityPanelStateService.CHECK_INTERVAL_MS), this.controllerResponses())
                        .pipe(exhaustMap(() => this.fetchNewest(accountId)));
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((report: ActivityReport | null) => this.onNewestChecked(report));
    }

    // =========================================================================================================================================================
    // Static Methods
    // =========================================================================================================================================================

    /**
     * The window the panel asks the report API for. The range goes to the server, not a browser-side filter, so
     * every page - and every Load More - only carries rows from that range: Today is local midnight to now,
     * Yesterday is the local day before, Last 7 Days is the 7 x 24 h ending now.
     */
    static loadWindow(range: ActivityPanelRange = 'last7Days'): { startDate: string; endDate: string } {
        const now = new Date();
        const startOfToday = new Date(now);
        startOfToday.setHours(0, 0, 0, 0);

        let start: Date;
        let end: Date = now;
        switch (range) {
            case 'today':
                start = startOfToday;
                break;
            case 'yesterday':
                start = new Date(startOfToday);
                start.setDate(start.getDate() - 1);
                end = startOfToday;
                break;
            default:
                start = new Date(now.getTime() - ActivityPanelStateService.WINDOW_DAYS * 24 * 60 * 60 * 1000);
                break;
        }

        return { startDate: start.toISOString(), endDate: end.toISOString() };
    }

    static allStatusesTicked(): Record<string, boolean> {
        return Object.fromEntries(Constants.ACTIVITY_STATUS_FILTER_ITEMS.map(item => [item.key, true]));
    }

    // =========================================================================================================================================================
    // Public Methods
    // =========================================================================================================================================================

    setRange(range: ActivityPanelRange): void {
        this._range.set(range);
    }

    setStatus(key: string, checked: boolean): void {
        this._statuses.update(current => ({ ...current, [key]: checked }));
    }

    clearAllStatuses(): void {
        this._statuses.update(current => Object.fromEntries(Object.keys(current).map(key => [key, false])));
    }

    selectAllStatuses(): void {
        this._statuses.set(ActivityPanelStateService.allStatusesTicked());
    }

    markOpened(): void {
        this._isPanelOpen.set(true);
        this._hasNewActivity.set(false);
        this.saveLastOpened();
    }

    markClosed(): void {
        // Whatever arrived while the panel was open has been seen.
        this._isPanelOpen.set(false);
        this.saveLastOpened();
        // The status filter is per open, not per session: every open starts with all statuses ticked.
        this.selectAllStatuses();
    }

    // =========================================================================================================================================================
    // Helper Methods
    // =========================================================================================================================================================

    private controllerResponses(): Observable<unknown> {
        const b = this.broadcastService;
        return merge(
            b.cc_manualOperationAction,
            b.cc_programStartOrStopAction,
            b.cc_setControllerConfiguration,
            b.cc_controllerRegistration,
            b.cc_controllerOutOfSync,
            b.cc_getAlerts,
        ).pipe(debounceTime(ActivityPanelStateService.APP_SYNC_DEBOUNCE_MS));
    }

    private fetchNewest(accountId: string): Observable<ActivityReport | null> {
        const { startDate, endDate } = ActivityPanelStateService.loadWindow();
        return this.activityReportManager.getActivityPage(accountId, startDate, endDate, 1)
            // A failed check only means no dot this round; the next tick tries again.
            .pipe(catchError(() => of(null)));
    }

    private onNewestChecked(report: ActivityReport | null): void {
        const accountId = this.accountId;
        const newest = report?.activities?.[0]?.timestamp;
        if (!accountId || !newest) { return; }

        const newestMs = Date.parse(newest);
        if (!Number.isFinite(newestMs)) { return; }

        if (this._isPanelOpen()) {
            this._newActivityWhileOpen.next(newestMs);
            return;
        }

        const lastOpened = this.localCache.getActivityPanelLastOpened(accountId);
        if (lastOpened == null) {
            // First time on this browser: treat what is already there as seen, so the dot means "new since".
            this.localCache.setActivityPanelLastOpened(accountId, newestMs);
            return;
        }

        this._hasNewActivity.set(newestMs > lastOpened);
    }

    private saveLastOpened(): void {
        if (this.accountId) {
            this.localCache.setActivityPanelLastOpened(this.accountId, Date.now());
        }
    }

    private resetSession(): void {
        this._range.set('last7Days');
        this.selectAllStatuses();
        this._hasNewActivity.set(false);
    }
}
